// Auto refresh (Yehuda, Nov 2; issue 291): the lists and the Record read their data again, quietly, when
// the browser tab comes back into view and every 60 seconds while it shows, so the VA sees the AE's
// answer without pressing refresh. Never while a popup is open, someone is typing in a field, or the
// page is already loading; scroll, tabs, filters and the open Quick view stay.
import { useEffect, useRef } from 'react';
import { useNavigation, useRevalidator } from 'react-router';
import { anyLayerOpen } from './modal-layer';

export const refreshMs = 60_000;
/** Coming back to the tab refreshes at once, but not again within this gap (focus and visibility both fire). */
export const backGapMs = 10_000;

export type RefreshCheck = {
  visible: boolean;
  layerOpen: boolean;
  typing: boolean;
  busy: boolean;
  /** When the page last read its data (loaded, saved or refreshed), ms. */
  lastAt: number;
  now: number;
  why: 'tick' | 'back';
};

/** Whether the page may read its data again now. */
export function mayRefresh(c: RefreshCheck): boolean {
  if (!c.visible || c.layerOpen || c.typing || c.busy) return false;
  return c.now - c.lastAt >= (c.why === 'back' ? backGapMs : refreshMs);
}

const typedInputs = new Set([
  'text',
  'search',
  'email',
  'number',
  'password',
  'tel',
  'url',
  'date',
]);

/** Whether the focused element takes typed text (a checkbox or a button does not). */
export function isTypingIn(el: Element | null): boolean {
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return tag === 'INPUT' && typedInputs.has((el as HTMLInputElement).type || 'text');
}

/**
 * Reads the page's loaders again on the rules above. `onRefresh` runs just before, for a page that
 * also reads something outside its loader (the notes).
 */
export function useAutoRefresh(onRefresh?: () => void) {
  const revalidator = useRevalidator();
  const navigation = useNavigation();
  // Any load counts, a save's or a page change's too, so a refresh never follows right after one.
  // Set when the page opens and whenever a load starts or ends.
  const lastAt = useRef(0);
  const busy = revalidator.state !== 'idle' || navigation.state !== 'idle';
  useEffect(() => {
    lastAt.current = Date.now();
  }, [busy]);
  const latest = useRef({ busy, revalidator, onRefresh });
  useEffect(() => {
    latest.current = { busy, revalidator, onRefresh };
  });

  useEffect(() => {
    const check = (why: RefreshCheck['why']) => {
      const now = Date.now();
      const ok = mayRefresh({
        visible: document.visibilityState === 'visible',
        layerOpen: anyLayerOpen(),
        typing: isTypingIn(document.activeElement),
        busy: latest.current.busy,
        lastAt: lastAt.current,
        now,
        why,
      });
      if (!ok) return;
      lastAt.current = now;
      latest.current.onRefresh?.();
      void latest.current.revalidator.revalidate();
    };
    // Checked every 5 seconds, so a refresh held back by a popup or typing follows soon after.
    const timer = setInterval(() => check('tick'), 5_000);
    const back = () => check('back');
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
    };
  }, []);
}
